import { gsap } from 'gsap';
import type { Container } from 'pixi.js';

/**
 * «Живые» анимации макетов (SPEC.md, 20.5): микробы покачиваются, босс дышит, элементы парят.
 * При «меньше движения» (настройка или `prefers-reduced-motion`) анимации не запускаются.
 */

let reduced = false;

export function setReducedMotion(value: boolean): void {
  reduced = value;
}

export function motionReduced(): boolean {
  return reduced;
}

export type IdleKind = 'corona' | 'adeno' | 'bacterium' | 'boss' | 'float' | 'talk';

/** Бесконечная анимация ожидания; возвращает функцию остановки. */
export function idle(target: Container, kind: IdleKind, delay = 0): () => void {
  if (reduced) return () => undefined;
  const base = {
    x: target.x,
    y: target.y,
    rotation: target.rotation,
    sx: target.scale.x,
    sy: target.scale.y,
  };
  const common = { yoyo: true, repeat: -1, ease: 'sine.inOut', delay: -delay };
  let tween: gsap.core.Tween | gsap.core.Timeline;
  switch (kind) {
    case 'corona':
      target.rotation = base.rotation - 0.1;
      tween = gsap.to(target, { rotation: base.rotation + 0.1, duration: 1.3, ...common });
      break;
    case 'adeno':
      tween = gsap.to(target, {
        y: base.y - 2,
        rotation: base.rotation + 0.09,
        duration: 1.1,
        ...common,
      });
      break;
    case 'bacterium':
      target.rotation = base.rotation - 0.09;
      tween = gsap.to(target, { rotation: base.rotation + 0.09, duration: 0.9, ...common });
      break;
    case 'boss':
      tween = gsap.to(target.scale, {
        x: base.sx * 1.04,
        y: base.sy * 0.96,
        duration: 1.3,
        ...common,
      });
      break;
    case 'float':
      tween = gsap.to(target, { y: base.y - 12, duration: 2, ...common });
      break;
    case 'talk':
      target.rotation = base.rotation - 0.017;
      tween = gsap.to(target, {
        rotation: base.rotation + 0.017,
        y: base.y - 3,
        duration: 1.1,
        ...common,
      });
      break;
  }
  return () => {
    tween.kill();
    if (target.destroyed) return;
    target.position.set(base.x, base.y);
    target.rotation = base.rotation;
    target.scale.set(base.sx, base.sy);
  };
}

/** Анимация микроба по его виду. */
export function microbeIdle(target: Container, color: number, delay = 0): () => void {
  const kind: IdleKind = color === 0 ? 'corona' : color === 1 ? 'adeno' : 'bacterium';
  return idle(target, kind, delay);
}
